import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Alert, Button, Form, Input, Modal, Tabs, Typography } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import './AuthModal.css';

const { Text } = Typography;

const AuthModal = ({ isOpen, onClose, defaultMode = 'login', redirectUrl = '' }) => {
  const [mode, setMode] = useState(defaultMode); // 'login', 'register', or 'reset'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    confirmPassword: '',
    invite_code: ''
  });
  const [error, setError] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  const { login, register } = useAuth();

  useEffect(() => {
    if (defaultMode) {
      setMode(defaultMode);
    }
  }, [defaultMode, isOpen]);



  if (!isOpen) return null;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (mode === 'register') {
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      const result = await register({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        invite_code: formData.invite_code
      });

      if (result.success) {
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }
        onClose();
      } else {
        setError(result.error);
      }
    } else if (mode === 'login') {
      const result = await login(formData.email, formData.password);

      if (result.success) {
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }
        onClose();
      } else {
        setError(result.error);
      }
    }

    setLoading(false);
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResetMessage('');

    try {
      await axios.post('/api/auth/password-reset', { email: resetEmail });
      setResetMessage('A new password has been sent to your email.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
      width={480}
      className="auth-modal-antd"
    >
      <div className="auth-modal-scroll">
        <div className="auth-modal-header">
          <h2>{mode === 'login' ? 'Welcome Back' : mode === 'register' ? 'Create Account' : 'Reset Password'}</h2>
          <p>{mode === 'login' ? 'Sign in to continue' : mode === 'register' ? 'Join PING today' : 'We will email you a new password'}</p>
        </div>

        {mode !== 'reset' && (
          <Tabs
            activeKey={mode}
            centered
            onChange={setMode}
            items={[
              { key: 'login', label: 'Login' },
              { key: 'register', label: 'Register' },
            ]}
          />
        )}

        {error && <Alert type="error" showIcon message={error} className="auth-alert" />}
        {resetMessage && <Alert type="success" showIcon message={resetMessage} className="auth-alert" />}

        {mode === 'reset' ? (
          <Form onSubmitCapture={handleResetSubmit} layout="vertical" className="auth-form">
            <Form.Item label="Email" required>
              <Input
                type="email"
                name="reset_email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Enter your email"
                prefix={<MailOutlined />}
                required
              />
            </Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              {loading ? 'Sending...' : 'Send New Password'}
            </Button>
            <div className="auth-footer">
              <Button type="link" onClick={() => setMode('login')}>Back to sign in</Button>
            </div>
          </Form>
        ) : (
          <Form onSubmitCapture={handleSubmit} layout="vertical" className="auth-form">
          {mode === 'register' && (
            <Form.Item label="Full Name" required>
              <Input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                placeholder="Enter your full name"
                prefix={<UserOutlined />}
                required
              />
            </Form.Item>
          )}

          <Form.Item label={mode === 'login' ? 'Email or Username' : 'Email'} required>
            <Input
              type={mode === 'login' ? 'text' : 'email'}
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={mode === 'login' ? 'Email or username' : 'Enter your email'}
              prefix={<MailOutlined />}
              required
            />
          </Form.Item>

          {mode === 'register' && (
            <Form.Item label="Invite Code (optional)">
              <Input
                type="text"
                name="invite_code"
                value={formData.invite_code}
                onChange={handleChange}
                placeholder="Optional invite code"
                prefix={<LockOutlined />}
              />
            </Form.Item>
          )}

          <Form.Item label="Password" required>
              <Input.Password
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                prefix={<LockOutlined />}
                required
              />
            {mode === 'register' && (
              <Text type="secondary">Password must be at least 8 characters</Text>
            )}
          </Form.Item>

          {mode === 'register' && (
            <Form.Item label="Confirm Password" required>
              <Input.Password
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                prefix={<LockOutlined />}
                required
              />
            </Form.Item>
          )}

          <Button type="primary" htmlType="submit" block loading={loading}>
            {loading ? 'Processing...' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </Button>
        </Form>
        )}

        {mode !== 'reset' && (
          <div className="auth-footer">
            {mode === 'login' ? (
              <p>
                Don't have an account?{' '}
                <Button type="link" onClick={() => setMode('register')}>Sign up</Button>
              </p>
            ) : (
              <p>
                Already have an account?{' '}
                <Button type="link" onClick={() => setMode('login')}>Sign in</Button>
              </p>
            )}
            {mode === 'login' && (
              <Button type="link" className="forgot-link" onClick={() => setMode('reset')}>
                Forgot password?
              </Button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AuthModal;
